import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText, AlertTriangle } from 'lucide-react';
import SiteFooter from '@/components/SiteFooter';

/**
 * Shared layout for all legal / policy / content pages.
 * Props:
 *  - title, lastUpdated, icon
 *  - draft (bool), draftNote (string) -> renders a prominent DRAFT banner
 *  - intro (node)
 *  - children (document body)
 */
export const LegalPageLayout = ({
  title,
  lastUpdated,
  icon: Icon = FileText,
  draft = false,
  draftNote,
  intro,
  children,
  testid = 'legal-page',
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid={testid}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700 safe-top">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-slate-400 hover:text-white"
            data-testid="back-to-app-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to App
          </Button>
          <div className="flex-1" />
          <Link to="/legal" className="text-xs sm:text-sm text-slate-400 hover:text-amber-300 transition-colors" data-testid="header-legal-center-link">
            Legal Center
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-amber-500/15 rounded-lg">
            <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-amber-400" />
          </div>
          <h1 className="text-2xl sm:text-4xl font-bold text-white">{title}</h1>
        </div>
        {lastUpdated && (
          <p className="text-sm text-slate-400 mb-6">Last Updated: {lastUpdated}</p>
        )}

        {draft && (
          <div className="mb-8 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 sm:p-5" data-testid="draft-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-300">DRAFT — OWNER / LEGAL REVIEW REQUIRED</p>
                <p className="text-sm text-amber-200/80 mt-1 leading-relaxed">
                  {draftNote || 'This document is a proposed draft and is not yet finalized. It must be reviewed and approved before being relied upon.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {intro && <div className="mb-8 text-slate-300 leading-relaxed">{intro}</div>}

        <div className="legal-prose space-y-8 text-slate-300 leading-relaxed">
          {children}
        </div>

        <p className="mt-12 text-sm text-slate-500">
          Questions about this document?{' '}
          <Link to="/contact" className="text-amber-300 hover:underline underline-offset-4" data-testid="prose-contact-link">Contact us</Link>.
        </p>
      </main>

      <SiteFooter />
    </div>
  );
};

/* Small presentational helpers for consistent document formatting */
export const Section = ({ title, children }) => (
  <section className="space-y-3">
    {title && <h2 className="text-xl sm:text-2xl font-semibold text-white">{title}</h2>}
    <div className="space-y-3">{children}</div>
  </section>
);

export const Callout = ({ tone = 'amber', title, children }) => {
  const tones = {
    amber: 'border-amber-500/30 bg-amber-500/10 text-amber-200',
    red: 'border-red-500/30 bg-red-500/10 text-red-200',
    blue: 'border-blue-500/30 bg-blue-500/10 text-blue-200',
  };
  return (
    <div className={`rounded-lg border p-4 ${tones[tone] || tones.amber}`}>
      {title && <p className="font-semibold mb-1">{title}</p>}
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
};

export const Bullets = ({ items }) => (
  <ul className="list-disc list-inside space-y-1.5 ml-1 marker:text-amber-500/70">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

export default LegalPageLayout;
